import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, Search, RefreshCw, Wifi, WifiOff, Coffee, Clock, Palmtree, Eye, EyeOff, History, Play } from 'lucide-react';
import HistoricoOperadorModal from '../../components/HistoricoOperadorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cores de eficiência baseadas na imagem
const getEficienciaColor = (eficiencia, mostrarCores = true) => {
  // Se cores desativadas, retornar cinza
  if (!mostrarCores) {
    return { bg: 'from-stone-300 to-stone-400', border: 'border-stone-300', text: 'text-stone-700' };
  }
  
  if (eficiencia >= 150) return { bg: 'from-blue-600 to-blue-700', border: 'border-blue-400', text: 'text-white' };
  if (eficiencia >= 120) return { bg: 'from-blue-400 to-blue-500', border: 'border-blue-300', text: 'text-white' };
  if (eficiencia >= 100) return { bg: 'from-green-400 to-green-500', border: 'border-green-300', text: 'text-white' };
  if (eficiencia >= 80) return { bg: 'from-yellow-400 to-yellow-500', border: 'border-yellow-300', text: 'text-stone-800' };
  if (eficiencia >= 50) return { bg: 'from-orange-400 to-orange-500', border: 'border-orange-300', text: 'text-white' };
  return { bg: 'from-red-400 to-red-500', border: 'border-red-300', text: 'text-white' };
};

const getEficienciaColorLegend = (range) => {
  const colors = {
    '0-49': 'bg-red-500',
    '50-79': 'bg-orange-500',
    '80-99': 'bg-yellow-500',
    '100-119': 'bg-green-500',
    '120-149': 'bg-blue-400',
    '150+': 'bg-blue-600'
  };
  return colors[range] || 'bg-stone-400';
};

export default function EstadoOperadores() {
  const [operadores, setOperadores] = useState([]);
  const [estadosOperadores, setEstadosOperadores] = useState([]);
  const [ausenciasHoje, setAusenciasHoje] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos'); // online, presentes, todos, pausa, ferias
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(new Date());
  const [mostrarCores, setMostrarCores] = useState(true); // Toggle cores de produtividade
  const [historicoOperador, setHistoricoOperador] = useState(null); // Operador para ver histórico
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);

  useEffect(() => {
    loadData();
    // Atualizar a cada 15 segundos para dados mais atualizados
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      // Carregar operadores
      const opResponse = await axios.get(`${API}/operadores`);
      
      // Carregar estados em tempo real
      const estadosResponse = await axios.get(`${API}/operadores/estados`);
      
      // Carregar ausências de hoje
      const hoje = new Date().toISOString().split('T')[0];
      const ausenciasResponse = await axios.get(`${API}/ausencias`, {
        params: { data_inicio: hoje, data_fim: hoje }
      });
      
      // Filtrar apenas ausências aprovadas que abrangem hoje
      const ausenciasAprovadas = (ausenciasResponse.data || []).filter(a => {
        const dataInicio = a.data_inicio?.split('T')[0];
        const dataFim = a.data_fim?.split('T')[0];
        return a.estado === 'aprovado' && dataInicio <= hoje && dataFim >= hoje;
      });
      
      setOperadores(opResponse.data);
      setEstadosOperadores(estadosResponse.data || []);
      setAusenciasHoje(ausenciasAprovadas);
      setUltimaAtualizacao(new Date());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Combinar operadores com estados e ausências
  const operadoresComEstado = operadores.map(op => {
    const estado = estadosOperadores.find(e => e.operador_id === op.id) || {};
    
    // Verificar se há ausência para este operador
    const ausencia = ausenciasHoje.find(a => a.operador_id === op.id);
    let estadoFinal = estado.estado || 'offline';
    let tipoAusencia = null;
    
    if (ausencia) {
      estadoFinal = 'ausente';
      tipoAusencia = ausencia.tipo; // ferias, falta, baixa_medica, licenca
    }
    
    return {
      ...op,
      estado: estadoFinal,
      tipoAusencia: tipoAusencia,
      eficiencia: estado.eficiencia_dia || 0,
      pecas_hoje: estado.pecas_hoje || 0,
      tempo_trabalhado: estado.tempo_trabalhado || 0,
      ultima_atividade: estado.ultima_atividade,
      // Tarefa atual
      operacao_atual: estado.operacao_atual,
      operacao_atual_codigo: estado.operacao_atual_codigo,
      of_atual: estado.of_atual,
      tarefa_atual_inicio: estado.tarefa_atual_inicio,
      // Última tarefa concluída
      ultima_tarefa_info: estado.ultima_tarefa_info
    };
  });

  // Filtrar por pesquisa
  const operadoresFiltrados = operadoresComEstado.filter(op => {
    const matchSearch = !searchTerm || 
      op.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      op.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchEstado = true;
    if (filtroEstado === 'online') {
      matchEstado = op.estado === 'online';
    } else if (filtroEstado === 'presentes') {
      matchEstado = ['online', 'pausa'].includes(op.estado);
    } else if (filtroEstado === 'pausa') {
      matchEstado = op.estado === 'pausa';
    } else if (filtroEstado === 'ferias') {
      matchEstado = op.estado === 'ausente' && op.tipoAusencia === 'ferias';
    } else if (filtroEstado === 'ausentes') {
      matchEstado = op.estado === 'ausente';
    }
    
    return matchSearch && matchEstado;
  });

  // Contar por estado
  const contagens = {
    online: operadoresComEstado.filter(op => op.estado === 'online').length,
    presentes: operadoresComEstado.filter(op => ['online', 'pausa'].includes(op.estado)).length,
    todos: operadoresComEstado.length,
    pausa: operadoresComEstado.filter(op => op.estado === 'pausa').length,
    ferias: operadoresComEstado.filter(op => op.estado === 'ausente' && op.tipoAusencia === 'ferias').length,
    ausentes: operadoresComEstado.filter(op => op.estado === 'ausente').length,
    offline: operadoresComEstado.filter(op => op.estado === 'offline').length
  };

  // Gerar código de operador
  const getCodigoOperador = (op) => {
    return `#${op.username?.replace(/[^0-9]/g, '') || op.id.slice(-5)}`;
  };

  // Formatar tempo trabalhado
  const formatarTempo = (segundos) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    return `${horas}h ${minutos}m`;
  };

  // Formatar hora ISO para HH:MM
  const formatarHora = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <GestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Estado dos Operadores</h1>
            <p className="text-stone-600">
              Monitorização em tempo real • Última atualização: {ultimaAtualizacao.toLocaleTimeString('pt-PT')}
            </p>
          </div>
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Filtros de Estado */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Tabs de Estado */}
            <div className="flex gap-2">
              <Button
                variant={filtroEstado === 'online' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('online')}
                className={`rounded-full gap-2 ${filtroEstado === 'online' ? 'bg-green-500 hover:bg-green-600' : ''}`}
              >
                <Wifi className="h-4 w-4" />
                Online <span className="ml-1 font-bold">{contagens.online}</span>
              </Button>
              <Button
                variant={filtroEstado === 'presentes' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('presentes')}
                className="rounded-full gap-2"
              >
                <Users className="h-4 w-4" />
                Presentes <span className="ml-1 font-bold">{contagens.presentes}</span>
              </Button>
              <Button
                variant={filtroEstado === 'todos' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('todos')}
                className="rounded-full gap-2"
              >
                Todos <span className="ml-1 font-bold">{contagens.todos}</span>
              </Button>
              <Button
                variant={filtroEstado === 'pausa' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('pausa')}
                className={`rounded-full gap-2 ${filtroEstado === 'pausa' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
              >
                <Coffee className="h-4 w-4" />
                Pausa <span className="ml-1 font-bold">{contagens.pausa}</span>
              </Button>
              <Button
                variant={filtroEstado === 'ferias' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('ferias')}
                className={`rounded-full gap-2 ${filtroEstado === 'ferias' ? 'bg-cyan-500 hover:bg-cyan-600' : ''}`}
              >
                <Palmtree className="h-4 w-4" />
                Férias <span className="ml-1 font-bold">{contagens.ferias}</span>
              </Button>
              <Button
                variant={filtroEstado === 'ausentes' ? 'default' : 'outline'}
                onClick={() => setFiltroEstado('ausentes')}
                className={`rounded-full gap-2 ${filtroEstado === 'ausentes' ? 'bg-red-500 hover:bg-red-600' : ''}`}
              >
                <Clock className="h-4 w-4" />
                Ausentes <span className="ml-1 font-bold">{contagens.ausentes}</span>
              </Button>
            </div>

            {/* Pesquisa e Toggle de Cores */}
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Pesquisar operador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Toggle Cores de Produtividade */}
              <Button
                variant={mostrarCores ? 'default' : 'outline'}
                onClick={() => setMostrarCores(!mostrarCores)}
                className={`gap-2 rounded-full ${mostrarCores ? 'bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600' : 'bg-stone-200 text-stone-600'}`}
                title={mostrarCores ? 'Ocultar cores de produtividade' : 'Mostrar cores de produtividade'}
              >
                {mostrarCores ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                {mostrarCores ? 'Cores ON' : 'Cores OFF'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Grid de Operadores */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {operadoresFiltrados.map(op => {
            // Cor baseada na última tarefa reportada (sempre)
            const efParaCor = op.ultima_tarefa_info?.eficiencia ?? op.eficiencia;
            const cores = getEficienciaColor(efParaCor, mostrarCores);
            const isOnline = op.estado === 'online';
            const isPausa = op.estado === 'pausa';
            const isAusente = op.estado === 'ausente';
            const isFerias = isAusente && op.tipoAusencia === 'ferias';
            const isFalta = isAusente && op.tipoAusencia === 'falta';
            const isBaixa = isAusente && op.tipoAusencia === 'baixa_medica';
            const isLicenca = isAusente && op.tipoAusencia === 'licenca';
            const isOffline = op.estado === 'offline';
            
            // Configuração de cores para ausências
            const getAusenciaConfig = () => {
              if (isFerias) return { bg: 'from-cyan-400 to-cyan-500', border: 'border-cyan-400', icon: Palmtree, label: 'Férias' };
              if (isFalta) return { bg: 'from-red-400 to-red-500', border: 'border-red-400', icon: Clock, label: 'Falta' };
              if (isBaixa) return { bg: 'from-amber-400 to-amber-500', border: 'border-amber-400', icon: Clock, label: 'Baixa' };
              if (isLicenca) return { bg: 'from-purple-400 to-purple-500', border: 'border-purple-400', icon: Clock, label: 'Licença' };
              return null;
            };
            
            const ausenciaConfig = getAusenciaConfig();

            // Tooltip da última tarefa
            const ut = op.ultima_tarefa_info;
            const tooltipTexto = ut 
              ? `Última: ${ut.codigo || ''} ${ut.nome || ''}\nTempo: ${formatarTempo(ut.tempo_segundos || 0)}\nQtd: ${ut.quantidade || 0} peças\nEficiência: ${Math.round(ut.eficiencia || 0)}%\nÀs ${formatarHora(ut.hora_fim)}`
              : '';
            
            return (
              <div
                key={op.id}
                className={`group relative rounded-2xl overflow-hidden border-4 transition-all hover:scale-105 hover:shadow-xl ${
                  ausenciaConfig ? ausenciaConfig.border :
                  isOffline && !ut ? 'border-stone-300' :
                  isPausa ? 'border-orange-400' :
                  cores.border
                }`}
                data-testid={`operador-card-${op.username}`}
                title={tooltipTexto}
              >
                {/* Card com gradiente */}
                <div className={`p-4 bg-gradient-to-br ${
                  ausenciaConfig ? ausenciaConfig.bg :
                  isOffline && !ut ? 'from-stone-300 to-stone-400' :
                  cores.bg
                }`}>
                  {/* Ícone de estado (canto superior esquerdo) */}
                  {isPausa && (
                    <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center">
                      <Coffee className="h-4 w-4 text-white" />
                    </div>
                  )}
                  {ausenciaConfig && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/20 px-2 py-0.5 rounded-full">
                      <ausenciaConfig.icon className="h-3 w-3 text-white" />
                      <span className="text-[10px] font-bold text-white uppercase">{ausenciaConfig.label}</span>
                    </div>
                  )}

                  {/* Eficiência (canto superior direito, antes do botão) */}
                  <div className={`absolute top-2 right-14 text-lg font-bold ${cores.text}`}>
                    {op.eficiencia > 0 ? `${Math.round(op.eficiencia)}%` : ''}
                  </div>

                  {/* Avatar do operador (apenas iniciais) */}
                  <div className="flex flex-col items-center pt-4">
                    <div className="relative">
                      <div className={`w-16 h-16 rounded-full overflow-hidden border-4 border-white/30 bg-white/20 flex items-center justify-center ${
                        isOffline && !ut ? 'grayscale opacity-60' : ''
                      }`}>
                        <span className={`text-2xl font-bold ${cores.text} opacity-80`}>
                          {op.nome_completo?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      {/* Indicador de online */}
                      {isOnline && (
                        <div className="absolute -bottom-1 right-0 w-5 h-5 rounded-full bg-green-400 border-2 border-white" />
                      )}
                      {isPausa && (
                        <div className="absolute -bottom-1 right-0 w-5 h-5 rounded-full bg-orange-400 border-2 border-white" />
                      )}
                    </div>

                    {/* Nome */}
                    <div className={`mt-3 text-xs font-semibold uppercase tracking-wide text-center ${cores.text} opacity-90 truncate w-full px-2`}>
                      {op.nome_completo?.split(' ').slice(0, 2).join(' ') || 'N/A'}
                    </div>

                    {/* Hora da última tarefa OU início da tarefa atual */}
                    {isOnline && op.tarefa_atual_inicio ? (
                      <div className={`flex items-center gap-1 mt-1 ${cores.text} opacity-80`}>
                        <Play className="h-3 w-3" />
                        <span className="text-[10px] font-medium">Início: {formatarHora(op.tarefa_atual_inicio)}</span>
                      </div>
                    ) : ut?.hora_fim ? (
                      <div className={`flex items-center gap-1 mt-1 ${cores.text} opacity-70`}>
                        <Clock className="h-3 w-3" />
                        <span className="text-[10px]">Última às {formatarHora(ut.hora_fim)}</span>
                      </div>
                    ) : null}

                    {/* Info extra peças */}
                    {op.pecas_hoje > 0 && (
                      <div className={`text-[10px] mt-1 ${cores.text} opacity-70`}>
                        {op.pecas_hoje} peças hoje
                      </div>
                    )}
                    
                    {/* Operação atual - AUMENTADO */}
                    {isOnline && op.operacao_atual && (
                      <div className="mt-2 px-3 py-2 bg-white/30 rounded-lg w-full">
                        <p className={`text-xs font-bold truncate text-center ${cores.text}`}>
                          {op.operacao_atual_codigo ? `${op.operacao_atual_codigo} - ` : ''}{op.operacao_atual}
                        </p>
                        {op.of_atual && (
                          <p className={`text-[10px] truncate text-center ${cores.text} opacity-70`}>
                            OF: {op.of_atual.numero}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Botão de Histórico - MAIOR */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setHistoricoOperador(op);
                        setShowHistoricoModal(true);
                      }}
                      className={`absolute top-1.5 right-1.5 p-2.5 rounded-full bg-white/30 hover:bg-white/60 transition-colors ${cores.text}`}
                      title="Ver histórico de operações"
                      data-testid={`historico-btn-${op.username}`}
                    >
                      <History className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Tooltip flutuante no hover — aparece ABAIXO do card, não tapa o conteúdo */}
                {ut && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-stone-900 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-3 pointer-events-none z-50 w-48 shadow-xl">
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-stone-900 rotate-45" />
                    <p className="text-white/50 text-[9px] uppercase tracking-wider mb-1">Última Tarefa</p>
                    <p className="text-white text-xs font-bold truncate">
                      {ut.codigo ? `${ut.codigo} - ` : ''}{ut.nome || '-'}
                    </p>
                    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
                      <span className="text-white/50">Tempo:</span>
                      <span className="text-white font-medium">{formatarTempo(ut.tempo_segundos || 0)}</span>
                      <span className="text-white/50">Qtd:</span>
                      <span className="text-white font-medium">{ut.quantidade || 0} pç</span>
                      <span className="text-white/50">Eficiência:</span>
                      <span className={`font-bold ${ut.eficiencia >= 100 ? 'text-green-400' : ut.eficiencia >= 80 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {Math.round(ut.eficiencia || 0)}%
                      </span>
                      <span className="text-white/50">Às:</span>
                      <span className="text-white font-medium">{formatarHora(ut.hora_fim)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sem resultados */}
        {operadoresFiltrados.length === 0 && (
          <Card className="p-12 text-center">
            <WifiOff className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">Nenhum operador encontrado com este filtro</p>
          </Card>
        )}

        {/* Legenda de Eficiência */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="text-sm text-stone-600 font-medium">Eficiência:</span>
            {[
              { range: '0-49', label: '0-49%' },
              { range: '50-79', label: '50-79%' },
              { range: '80-99', label: '80-99%' },
              { range: '100-119', label: '100-119%' },
              { range: '120-149', label: '120-149%' },
              { range: '150+', label: '150%+' },
            ].map(item => (
              <div key={item.range} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full ${getEficienciaColorLegend(item.range)}`} />
                <span className="text-xs text-stone-600">{item.label}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <Wifi className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{contagens.online}</p>
                <p className="text-xs text-green-600">A trabalhar agora</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                <Coffee className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{contagens.pausa}</p>
                <p className="text-xs text-orange-600">Em pausa</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-cyan-50 border-cyan-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center">
                <Palmtree className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-cyan-700">{contagens.ferias}</p>
                <p className="text-xs text-cyan-600">De férias</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-stone-50 border-stone-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-stone-400 flex items-center justify-center">
                <WifiOff className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-stone-700">{contagens.offline}</p>
                <p className="text-xs text-stone-600">Offline</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Modal de Histórico */}
      <HistoricoOperadorModal
        operador={historicoOperador}
        isOpen={showHistoricoModal}
        onClose={() => {
          setShowHistoricoModal(false);
          setHistoricoOperador(null);
        }}
      />
    </GestorLayout>
  );
}
